import { useCallback, useMemo } from 'react'
import { compose, hoc, useStore } from '@'
import { check } from 'components/can'
import GetRegionCoverage from '../../actions/GetRegionCoverage'
import DeleteRegion from '../../actions/DeleteRegion'
import UpdateRegion from '../../actions/UpdateRegion'
import OpenRegionEditor from '../../actions/OpenRegionEditor'
import OpenRegionHotelsModal from '../../actions/OpenRegionHotelsModal'
import { $regionCoverage, $regionCoverageLoading, $regionCoverageQuery } from '../../stores'
import { DEFAULT_PAGE_SIZE, RESOURCE, confirmDanger, useUrlQuery } from '../CoverageCommon'

const QUERY_FIELDS = ['region_id', 'supplier_code']

const loadRegion = query => GetRegionCoverage({ page: 1, page_size: DEFAULT_PAGE_SIZE, ...query })

const container = compose(
    hoc(props => {
        const [regionCoverage, loading, query] = useStore([
            $regionCoverage,
            $regionCoverageLoading,
            $regionCoverageQuery
        ])

        useUrlQuery($regionCoverageQuery, QUERY_FIELDS, loadRegion)

        const perms = useMemo(
            () => ({
                create: check(RESOURCE, 'create'),
                edit: check(RESOURCE, 'edit'),
                delete: check(RESOURCE, 'delete')
            }),
            []
        )

        const supplierCode = (query && query.supplier_code) || undefined

        const handlePage = useCallback((page, pageSize) => {
            GetRegionCoverage({ ...$regionCoverageQuery.getState(), page, page_size: pageSize })
        }, [])

        const handleCreate = useCallback(() => {
            OpenRegionHotelsModal({ mode: 'create' }).catch(console.error)
        }, [])

        const handleCreateFor = useCallback(
            regionId => {
                OpenRegionHotelsModal({ mode: 'create', region_id: regionId, supplier_code: supplierCode }).catch(
                    console.error
                )
            },
            [supplierCode]
        )

        const handleAddHotels = useCallback(
            regionId => {
                OpenRegionHotelsModal({ mode: 'add', region_id: regionId, supplier_code: supplierCode }).catch(
                    console.error
                )
            },
            [supplierCode]
        )

        const handleEdit = useCallback(
            region => {
                OpenRegionEditor(region, supplierCode).catch(console.error)
            },
            [supplierCode]
        )

        const handleDelete = useCallback(
            region => {
                const rows = (region.supplier_codes || []).reduce((sum, item) => sum + item.rows, 0)
                confirmDanger({
                    title: supplierCode
                        ? `Delete the ${supplierCode} rows of region ${region.region_id}?`
                        : `Delete region ${region.region_id}?`,
                    content: supplierCode
                        ? `Only hotel_mapping_v2 rows labelled ${supplierCode} are deleted; other sources stay. Switch the source to All to delete every row.`
                        : `Deletes all ${rows} hotel_mapping_v2 row(s) of this region, every source. Search for this region will return no hotels.`,
                    okText: 'Delete',
                    onOk: () => DeleteRegion(region.region_id, supplierCode)
                })
            },
            [supplierCode]
        )

        const handleRemoveHotels = useCallback(
            (region, hotelIds) => {
                const body = { remove_hotel_ids: hotelIds }
                if (supplierCode) {
                    body.supplier_code = supplierCode
                }
                confirmDanger({
                    title: `Remove ${hotelIds.length} hotel(s) from region ${region.region_id}?`,
                    content: supplierCode
                        ? `Only their ${supplierCode} links are deleted.`
                        : 'Their links under every source are deleted. Search for this region will stop returning them.',
                    okText: 'Remove',
                    onOk: () => UpdateRegion(region.region_id, body)
                })
            },
            [supplierCode]
        )

        return {
            regionCoverage,
            loading,
            query,
            supplierCode,
            perms,
            handlePage,
            handleCreate,
            handleCreateFor,
            handleAddHotels,
            handleEdit,
            handleDelete,
            handleRemoveHotels,
            ...props
        }
    })
)

export default container
